import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import { Card, Grid, Typography, CardContent, Backdrop } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { NavLink } from 'react-router-dom';
import { useState, useRef } from 'react';
// utils
import { fDate } from '../../../utils/formatTime';


// ----------------------------------------------------------------------

const StyledCardMedia = styled('div')({
  position: 'relative',
  paddingTop: 'calc(100% * 3 / 4)',
  overflow: 'hidden',
});

const StyledTitle = styled(Typography)({
  height: 44,
  overflow: 'hidden',
  WebkitLineClamp: 2,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
});

const StyledCover = styled('img')({
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
  transition: 'transform 0.4s ease',
  '.MuiCard-root:hover &': {
    transform: 'scale(1.05)',
  },
});

const StyledVideo = styled('video')({
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
});

const PlayIconOverlay = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1,
  pointerEvents: 'none',
  opacity: 0.9,
  transition: 'opacity 0.2s ease',
  '.MuiCard-root:hover &': {
    opacity: 1,
  },
});

// ----------------------------------------------------------------------

BlogPostCard.propTypes = {
  post: PropTypes.object.isRequired,
  index: PropTypes.number,
  cover: PropTypes.string,
  isPost: PropTypes.bool
};

export default function BlogPostCard({ post, index, cover: sourceUrl, isPost }) {
  const videoPlayer = useRef();

  let title = (isPost) ? '' : post.folder_name;
  const createdAt = (isPost) ? '' : fDate(post.last_modified_date);
  const latestPostLarge = index === 0;
  const latestPost = index >= 1;
  const navigationLink = `/${post.folder_name}/?date=${createdAt}`;

  const [isImageOpen, setImageOpen] = useState(false);

  const isImage = !sourceUrl.endsWith('.mp4');
  if (!isImage) {
    title = <PlayCircleOutlineIcon fontSize='large' />;
  }

  const handleImageOpen = () => {
    setImageOpen(true);
  };

  const imageAltText = `${post.folder_name} onlyfans leaks, leak, leaked, nude, girl, pretty woman`;

  return (
    <Grid item xs={12} sm={latestPostLarge ? 12 : 6} md={latestPostLarge ? 6 : 3}>
      {/* POP UP view */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0,0,0,0.75)',
        }}
        open={isImageOpen}
        onClick={() => { if (isImage) { setImageOpen(false); } }}
      >
        {(isImage)
          ? <StyledCover
              sx={{
                p: { xs: '8px', sm: '20px' },
                width: { xs: '100%', sm: '90%' },
                objectFit: 'contain',
                position: 'absolute',
                transform: 'none',
                '.MuiCard-root:hover &': { transform: 'none' },
              }}
              src={sourceUrl}
              alt={imageAltText}
            />
          // eslint-disable-next-line jsx-a11y/media-has-caption
          : <StyledVideo
              ref={videoPlayer}
              sx={{
                top: '5%',
                width: { xs: '100%', sm: '80%', md: '60%' },
                height: { xs: 'auto', md: 650 },
                objectFit: 'contain',
              }}
              controls
            >
              <source src={sourceUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </StyledVideo>
        }

        <CloseIcon
          fontSize='large'
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            cursor: 'pointer',
            transition: 'transform 0.2s ease, opacity 0.2s ease',
            opacity: 0.8,
            '&:hover': { opacity: 1, transform: 'scale(1.15)' },
          }}
          onClick={() => {
            setImageOpen(false);
            if (!isImage) { videoPlayer.current.pause(); }
          }}
        />
      </Backdrop>

      <Card sx={{ position: 'relative' }}>
        <NavLink to={navigationLink} onClick={handleImageOpen} style={{ textDecoration: 'none' }}>
          <StyledCardMedia
            sx={{
              ...((latestPostLarge || latestPost) && {
                pt: 'calc(100% * 4 / 3)',
                '&:after': {
                  top: 0,
                  content: "''",
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  background: isPost
                    ? 'none'
                    : 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)',
                },
              }),
              ...(latestPostLarge && {
                pt: {
                  xs: 'calc(100% * 4 / 3)',
                  sm: 'calc(100% * 3 / 4.66)',
                },
              }),
            }}
          >
            {(isImage)
              ? <StyledCover src={sourceUrl} alt={imageAltText} />
              : <>
                  <StyledVideo>
                    <source src={sourceUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </StyledVideo>
                  <PlayIconOverlay>
                    <PlayCircleOutlineIcon sx={{ fontSize: 56, color: '#fff', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))' }} />
                  </PlayIconOverlay>
                </>
            }
          </StyledCardMedia>
          <CardContent
            sx={{
              pt: 4,
              ...((latestPostLarge || latestPost) && {
                bottom: 0,
                width: '100%',
                position: 'absolute',
              }),
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.5 }}>
              {createdAt}
            </Typography>

            <StyledTitle
              color="inherit"
              variant="subtitle1"
              sx={{
                ...(latestPostLarge && { typography: 'h4', height: 60 }),
                ...((latestPostLarge || latestPost) && {
                  color: 'common.white',
                  fontWeight: 600,
                  textShadow: '0 1px 4px rgba(0,0,0,0.5)',
                }),
              }}
            >
              {title}
            </StyledTitle>
          </CardContent>
        </NavLink>
      </Card>
    </Grid>
  );
}
